import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Modal, Spinner } from 'react-bootstrap';
import {
  confirmPaymentMethods,
  createSetupIntent,
  deletePaymentMethod,
  extractPaymentMethods,
  listPaymentMethods,
  setDefaultPaymentMethod,
  syncPaymentMethods,
} from '../../services/billingService';
import { useStripeConfig } from './VolksignStripeProvider';
import { StripeElementsWrapper } from './VolksignStripeProvider';
import StripePaymentForm from './StripePaymentForm';

const STRIPE_RETURN_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
  'session_id',
];

const PaymentMethodsCard = () => {
  const { ready: stripeReady, error: stripeConfigError } = useStripeConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [methods, setMethods] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [billingContact, setBillingContact] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const stripeReturnHandled = useRef(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  const [setAsDefault, setSetAsDefault] = useState(true);

  const applyPaymentMethodsPayload = useCallback((data) => {
    const { methods: list, defaultId: nextDefaultId } = extractPaymentMethods(data);
    setMethods(list);
    setDefaultId(nextDefaultId);
    setBillingContact(data?.billing_contact ?? data?.billingContact ?? null);
  }, []);

  const loadMethods = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let data = await listPaymentMethods();
      let { methods: list, defaultId: nextDefaultId } = extractPaymentMethods(data);

      // Backend repair: reconcile org customer with card on billing-contact Stripe customer
      if (nextDefaultId && list.length === 0) {
        data = await syncPaymentMethods();
        ({ methods: list, defaultId: nextDefaultId } = extractPaymentMethods(data));
      }

      applyPaymentMethodsPayload(data);
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err?.message
        || 'Could not load payment methods.'
      );
    } finally {
      setLoading(false);
    }
  }, [applyPaymentMethodsPayload]);

  const syncFromStripeReturn = useCallback(async () => {
    const paymentIntentId = searchParams.get('payment_intent');
    const setupIntentId = searchParams.get('setup_intent');
    const checkoutSessionId = searchParams.get('session_id');

    if (!paymentIntentId && !setupIntentId && !checkoutSessionId) {
      return false;
    }

    setSyncing(true);
    setError('');
    try {
      const data = await syncPaymentMethods({
        paymentIntentId: paymentIntentId || undefined,
        setupIntentId: setupIntentId || undefined,
        checkoutSessionId: checkoutSessionId || undefined,
      });
      applyPaymentMethodsPayload(data);
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err?.message
        || 'Could not sync your saved card.'
      );
      await loadMethods();
    } finally {
      setSyncing(false);
      const next = new URLSearchParams(searchParams);
      STRIPE_RETURN_PARAMS.forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }

    return true;
  }, [searchParams, setSearchParams, applyPaymentMethodsPayload, loadMethods]);

  useEffect(() => {
    if (stripeReturnHandled.current) return undefined;

    const paymentIntentId = searchParams.get('payment_intent');
    const setupIntentId = searchParams.get('setup_intent');
    const checkoutSessionId = searchParams.get('session_id');

    if (paymentIntentId || setupIntentId || checkoutSessionId) {
      stripeReturnHandled.current = true;
      syncFromStripeReturn();
      return undefined;
    }

    loadMethods();
    return undefined;
  }, [searchParams, syncFromStripeReturn, loadMethods]);

  const openAddModal = async () => {
    setShowAddModal(true);
    setClientSecret(null);
    setSetupLoading(true);
    setSavePaymentMethod(true);
    setSetAsDefault(true);
    try {
      const data = await createSetupIntent({ setAsDefault });
      const secret = data?.client_secret ?? data?.clientSecret;
      if (!secret) {
        throw new Error('Setup intent client secret was not returned.');
      }
      setClientSecret(secret);
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err?.message
        || 'Could not start card setup.'
      );
      setShowAddModal(false);
    } finally {
      setSetupLoading(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setClientSecret(null);
  };

  const handleCardSaved = async ({ setupIntentId } = {}) => {
    if (setupIntentId && savePaymentMethod) {
      try {
        const data = await confirmPaymentMethods({
          setupIntentId,
          setAsDefault: setAsDefault && savePaymentMethod,
        });
        applyPaymentMethodsPayload(data);
        closeAddModal();
        return;
      } catch (err) {
        setError(
          err?.response?.data?.message
          || err?.message
          || 'Card was saved in Stripe but could not be linked to your account.'
        );
      }
    }
    closeAddModal();
    await loadMethods();
  };

  const handleManualSync = async () => {
    setSyncing(true);
    setError('');
    try {
      const data = await syncPaymentMethods();
      applyPaymentMethodsPayload(data);
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err?.message
        || 'Could not sync payment methods.'
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleSetDefault = async (id) => {
    setBusyId(id);
    try {
      await setDefaultPaymentMethod(id);
      await loadMethods();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not update default card.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this payment method?')) return;
    setBusyId(id);
    try {
      await deletePaymentMethod(id);
      await loadMethods();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not remove card.');
    } finally {
      setBusyId(null);
    }
  };

  const formatBrand = (pm) => {
    const brand = String(pm.brand || 'card').replace(/_/g, ' ');
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const showEmptyState = !loading && !syncing && methods.length === 0;
  const hasOrphanDefault = showEmptyState && Boolean(defaultId);

  return (
    <>
      <Card className="shadow-sm mt-4">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h5 className="fw-semibold mb-1">Payment methods</h5>
              <p className="text-muted small mb-0">
                Cards used for plan upgrades and pay-per-envelope sends.
                {billingContact?.name && (
                  <>
                    {' '}
                    Billing contact: {billingContact.name}
                    {billingContact.email ? ` (${billingContact.email})` : ''}.
                  </>
                )}
              </p>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={openAddModal}
              disabled={!stripeReady || syncing}
            >
              Add card
            </Button>
          </div>

          {stripeConfigError && (
            <Alert variant="warning" className="small">
              {stripeConfigError}
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="small d-flex justify-content-between">
              <span>{error}</span>
              <Button size="sm" variant="outline-danger" onClick={loadMethods}>
                Retry
              </Button>
            </Alert>
          )}

          {(loading || syncing) && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" variant="primary" />
              {syncing && (
                <p className="text-muted small mt-2 mb-0">Syncing your saved card…</p>
              )}
            </div>
          )}

          {showEmptyState && !hasOrphanDefault && (
            <p className="text-muted small mb-0">No saved cards yet.</p>
          )}

          {hasOrphanDefault && (
            <Alert variant="info" className="small d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span>
                A default card is on file but details are not available yet. Sync to refresh
                the list.
              </span>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleManualSync}
                disabled={syncing}
              >
                Sync card
              </Button>
            </Alert>
          )}

          {!loading && !syncing && methods.length > 0 && (
            <ul className="list-group list-group-flush">
              {methods.map((pm) => {
                const id = pm.id;
                const isDefault = id === defaultId || pm.is_default === true;
                const hasCardDetails = pm.last4 || pm.brand;
                return (
                  <li
                    key={id}
                    className="list-group-item px-0 d-flex flex-wrap align-items-center justify-content-between gap-2"
                  >
                    <div>
                      {hasCardDetails ? (
                        <>
                          <span className="fw-medium">{formatBrand(pm)}</span>
                          <span className="text-muted ms-2">•••• {pm.last4}</span>
                          {pm.exp_month && pm.exp_year && (
                            <span className="text-muted small ms-2">
                              {pm.exp_month}/{pm.exp_year}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="fw-medium">Saved card</span>
                      )}
                      {isDefault && (
                        <Badge bg="primary" className="ms-2">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      {!isDefault && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={busyId === id}
                          onClick={() => handleSetDefault(id)}
                        >
                          Set default
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={busyId === id}
                        onClick={() => handleDelete(id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={closeAddModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add payment method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {setupLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {!setupLoading && clientSecret && (
            <StripeElementsWrapper clientSecret={clientSecret}>
              <StripePaymentForm
                mode="setup"
                submitLabel="Save card"
                showSaveOptions
                savePaymentMethod={savePaymentMethod}
                onSavePaymentMethodChange={setSavePaymentMethod}
                setAsDefault={setAsDefault}
                onSetAsDefaultChange={setSetAsDefault}
                onSuccess={handleCardSaved}
              />
            </StripeElementsWrapper>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PaymentMethodsCard;
